import * as THREE from 'three';
import useEditorStore from '../stores/useEditorStore';

export const PREPARING_SCAN_EULER_ORDER = 'XYZ';

function roundDegFromRad(rad) {
  return Math.round(THREE.MathUtils.radToDeg(rad) * 100) / 100;
}

/** Degrees in XYZ order; does not mutate mesh.rotation. */
export function readPreparingScanEulerDegrees(mesh) {
  if (!mesh) return { x: 0, y: 0, z: 0 };
  const e = mesh.rotation.clone();
  e.reorder(PREPARING_SCAN_EULER_ORDER);
  return {
    x: roundDegFromRad(e.x),
    y: roundDegFromRad(e.y),
    z: roundDegFromRad(e.z),
  };
}

export function applyPreparingScanEulerDegrees(mesh, deg) {
  if (!mesh || !deg) return;
  mesh.rotation.order = PREPARING_SCAN_EULER_ORDER;
  mesh.rotation.set(
    THREE.MathUtils.degToRad(deg.x),
    THREE.MathUtils.degToRad(deg.y),
    THREE.MathUtils.degToRad(deg.z),
    PREPARING_SCAN_EULER_ORDER,
  );
  mesh.updateMatrixWorld(true);
}

/** Copy position / quaternion / scale from one mesh to another. */
export function copyMeshTransform(source, target) {
  if (!source || !target) return;
  target.position.copy(source.position);
  target.quaternion.copy(source.quaternion);
  target.scale.copy(source.scale);
  if (source.rotation?.order) {
    target.rotation.order = source.rotation.order;
  }
  target.updateMatrixWorld(true);
}

export function capturePreparingScanSocketRotationFromMesh(mesh) {
  if (!mesh) return;
  const deg = readPreparingScanEulerDegrees(mesh);
  useEditorStore.getState().setPreparingScanSocketEulerDeg?.(deg);
}

export function restorePreparingScanSocketRotation(mesh) {
  if (!mesh) return;
  const deg = useEditorStore.getState().preparingScanSocketEulerDeg;
  if (!deg) return;
  applyPreparingScanEulerDegrees(mesh, deg);
}

/** Detach gizmo without losing the object's current TRS (TransformControls can reset on detach). */
export function detachTransformControlsPreserveObject(tc) {
  if (!tc) return;
  const object = tc.object;
  if (!object) {
    tc.detach();
    return;
  }
  object.updateMatrixWorld(true);
  const position = object.position.clone();
  const quaternion = object.quaternion.clone();
  const scale = object.scale.clone();
  const order = object.rotation.order;
  tc.detach();
  object.position.copy(position);
  object.quaternion.copy(quaternion);
  object.scale.copy(scale);
  object.rotation.setFromQuaternion(object.quaternion, order);
  object.updateMatrixWorld(true);
}
